import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { 
  Euro, CheckCircle, AlertCircle, Copy, Building, Calendar, 
  FileText, Info, CreditCard, Banknote
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MesCharges = () => {
  const { token } = useAuth();
  const [charges, setCharges] = useState([]);
  const [summary, setSummary] = useState({ total_du: 0, total_paye: 0, solde_total: 0 });
  const [loading, setLoading] = useState(true);
  const [selectedCharge, setSelectedCharge] = useState(null);

  const fetchCharges = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/comptabilite/mes-charges`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCharges(data.charges || []);
        setSummary({
          total_du: data.total_du || 0,
          total_paye: data.total_paye || 0,
          solde_total: data.solde_total || 0
        });
      }
    } catch (error) {
      console.error('Error fetching charges:', error);
      toast.error('Erreur lors du chargement des charges');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchCharges();
  }, [fetchCharges]);

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copié dans le presse-papier`);
  };

  const generateReference = (charge) => {
    // Génère une référence de virement unique : APPEL-NUMERO-DATE
    const dateStr = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    return `${charge.numero || 'CHARGE'}-${dateStr}`;
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  const chargesImpayees = charges.filter(c => c.solde > 0);
  const chargesPayees = charges.filter(c => c.solde <= 0);

  return (
    <div className="space-y-6" data-testid="mes-charges-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Mes Charges</h1>
          <p className="text-gray-500 text-sm">Consultez vos charges de copropriété et effectuez vos paiements</p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl p-6 shadow-sm border">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-blue-100 rounded-xl">
              <Euro className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total appelé</p>
              <p className="text-2xl font-bold">{summary.total_du.toLocaleString('fr-FR')}€</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl p-6 shadow-sm border">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-green-100 rounded-xl">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total payé</p>
              <p className="text-2xl font-bold text-green-600">{summary.total_paye.toLocaleString('fr-FR')}€</p>
            </div>
          </div>
        </div>
        
        <div className={`rounded-xl p-6 shadow-sm border ${summary.solde_total > 0 ? 'bg-orange-50 border-orange-200' : 'bg-green-50 border-green-200'}`}>
          <div className="flex items-center gap-4">
            <div className={`p-3 rounded-xl ${summary.solde_total > 0 ? 'bg-orange-100' : 'bg-green-100'}`}>
              {summary.solde_total > 0 ? (
                <AlertCircle className="w-6 h-6 text-orange-600" />
              ) : (
                <CheckCircle className="w-6 h-6 text-green-600" />
              )}
            </div>
            <div>
              <p className="text-sm text-gray-500">Solde restant</p>
              <p className={`text-2xl font-bold ${summary.solde_total > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                {summary.solde_total.toLocaleString('fr-FR')}€
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Charges impayées */}
      {chargesImpayees.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
          <div className="bg-orange-50 px-6 py-4 border-b border-orange-100">
            <h2 className="font-semibold text-orange-800 flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              Charges à payer ({chargesImpayees.length})
            </h2>
          </div>
          
          <div className="divide-y">
            {chargesImpayees.map(charge => (
              <div key={charge.id} className="p-6" data-testid={`charge-item-${charge.id}`}>
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-gray-900">{charge.libelle}</h3>
                      <Badge variant="outline" className="bg-orange-100 text-orange-700 border-orange-200">
                        À payer
                      </Badge>
                    </div>
                    <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <Building className="w-4 h-4" />
                        {charge.building_name}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        Échéance: {new Date(charge.date_echeance).toLocaleDateString('fr-FR')}
                      </span>
                      <span className="flex items-center gap-1">
                        <FileText className="w-4 h-4" />
                        {charge.numero}
                      </span>
                    </div>
                    {charge.periode && (
                      <p className="text-sm text-gray-400 mt-1">{charge.periode}</p>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-6">
                    <div className="text-right">
                      <p className="text-sm text-gray-500">Montant dû</p>
                      <p className="text-xl font-bold text-gray-900">{charge.montant_du.toLocaleString('fr-FR')}€</p>
                      {charge.montant_paye > 0 && (
                        <p className="text-xs text-green-600">
                          Déjà payé: {charge.montant_paye.toLocaleString('fr-FR')}€
                        </p>
                      )}
                    </div>
                    
                    <div className="text-right">
                      <p className="text-sm text-gray-500">Solde</p>
                      <p className="text-xl font-bold text-orange-600">{charge.solde.toLocaleString('fr-FR')}€</p>
                    </div>
                    
                    <Button 
                      onClick={() => setSelectedCharge(selectedCharge?.id === charge.id ? null : charge)}
                      variant={selectedCharge?.id === charge.id ? "default" : "outline"}
                      className={selectedCharge?.id === charge.id ? "bg-teal-500 hover:bg-teal-600" : ""}
                      data-testid={`show-rib-btn-${charge.id}`}
                    >
                      <Banknote className="w-4 h-4 mr-2" />
                      {selectedCharge?.id === charge.id ? "Masquer RIB" : "Voir RIB"}
                    </Button>
                  </div>
                </div>

                {/* Section RIB/IBAN */}
                {selectedCharge?.id === charge.id && (
                  <div className="mt-4 p-5 bg-blue-50 rounded-xl border border-blue-200">
                    <h4 className="font-semibold text-blue-800 mb-4 flex items-center gap-2">
                      <CreditCard className="w-5 h-5" />
                      Coordonnées bancaires pour le virement
                    </h4>
                    
                    {charge.banque_iban ? (
                      <div className="space-y-4">
                        <div className="bg-white rounded-lg p-4 border border-blue-100">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {charge.banque_titulaire && (
                              <div>
                                <p className="text-xs text-blue-600 uppercase tracking-wider mb-1">Bénéficiaire</p>
                                <p className="font-medium text-gray-900">{charge.banque_titulaire}</p>
                              </div>
                            )}
                            {charge.banque_nom && (
                              <div>
                                <p className="text-xs text-blue-600 uppercase tracking-wider mb-1">Banque</p>
                                <p className="font-medium text-gray-900">{charge.banque_nom}</p>
                              </div>
                            )}
                            <div className="md:col-span-2">
                              <p className="text-xs text-blue-600 uppercase tracking-wider mb-1">IBAN</p>
                              <div className="flex items-center gap-2">
                                <p className="font-mono font-bold text-gray-900 tracking-wider text-lg">
                                  {charge.banque_iban}
                                </p>
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => copyToClipboard(charge.banque_iban.replace(/\s/g, ''), 'IBAN')}
                                  className="text-blue-600 hover:text-blue-700"
                                >
                                  <Copy className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                            {charge.banque_bic && (
                              <div>
                                <p className="text-xs text-blue-600 uppercase tracking-wider mb-1">BIC/SWIFT</p>
                                <div className="flex items-center gap-2">
                                  <p className="font-mono font-medium text-gray-900">{charge.banque_bic}</p>
                                  <Button 
                                    variant="ghost" 
                                    size="sm"
                                    onClick={() => copyToClipboard(charge.banque_bic, 'BIC')}
                                    className="text-blue-600 hover:text-blue-700"
                                  >
                                    <Copy className="w-4 h-4" />
                                  </Button>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Référence à indiquer */}
                        <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                          <p className="text-xs text-yellow-700 uppercase tracking-wider mb-1 flex items-center gap-1">
                            <Info className="w-3 h-3" />
                            Référence à indiquer dans le libellé du virement
                          </p>
                          <div className="flex items-center gap-2">
                            <p className="font-mono font-bold text-yellow-800 text-lg">
                              {generateReference(charge)}
                            </p>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => copyToClipboard(generateReference(charge), 'Référence')}
                              className="text-yellow-700 hover:text-yellow-800"
                            >
                              <Copy className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>

                        {/* Montant à payer */}
                        <div className="bg-teal-50 rounded-lg p-4 border border-teal-200">
                          <p className="text-xs text-teal-700 uppercase tracking-wider mb-1">Montant à virer</p>
                          <p className="font-bold text-teal-800 text-2xl">
                            {charge.solde.toLocaleString('fr-FR')}€
                          </p>
                        </div>

                        <p className="text-xs text-gray-500 italic">
                          Veuillez vérifier que l'IBAN correspond bien à celui de votre copropriété avant d'effectuer le virement.
                        </p>
                      </div>
                    ) : (
                      <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200 text-center">
                        <AlertCircle className="w-8 h-8 text-yellow-600 mx-auto mb-2" />
                        <p className="text-yellow-800 font-medium">Coordonnées bancaires non renseignées</p>
                        <p className="text-yellow-600 text-sm mt-1">
                          Veuillez contacter votre gestionnaire pour obtenir les coordonnées bancaires de la copropriété.
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Charges payées */}
      {chargesPayees.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
          <div className="bg-green-50 px-6 py-4 border-b border-green-100">
            <h2 className="font-semibold text-green-800 flex items-center gap-2">
              <CheckCircle className="w-5 h-5" />
              Charges payées ({chargesPayees.length})
            </h2>
          </div>
          
          <div className="divide-y">
            {chargesPayees.map(charge => (
              <div key={charge.id} className="p-6 bg-green-50/30" data-testid={`charge-paid-${charge.id}`}>
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-gray-700">{charge.libelle}</h3>
                      <Badge className="bg-green-100 text-green-700">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Payé
                      </Badge>
                    </div>
                    <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <Building className="w-4 h-4" />
                        {charge.building_name}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {new Date(charge.date_emission).toLocaleDateString('fr-FR')}
                      </span>
                    </div>
                  </div>
                  
                  <div className="text-right">
                    <p className="text-sm text-gray-500">Montant</p>
                    <p className="text-xl font-bold text-green-600">{charge.montant_du.toLocaleString('fr-FR')}€</p>
                  </div>
                </div>
                
                {charge.paiements?.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-green-100">
                    <p className="text-xs text-gray-500 mb-2">Historique des paiements:</p>
                    <div className="flex flex-wrap gap-2">
                      {charge.paiements.map((p, i) => (
                        <Badge key={i} variant="outline" className="text-xs bg-white">
                          {new Date(p.date_paiement).toLocaleDateString('fr-FR')} - {p.montant.toLocaleString('fr-FR')}€
                          {p.type_paiement && ` (${p.type_paiement})`}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty state */}
      {charges.length === 0 && (
        <div className="bg-white rounded-xl p-12 text-center shadow-sm border">
          <Euro className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-700 mb-2">Aucune charge</h3>
          <p className="text-gray-500">Vous n'avez pas de charges à payer pour le moment.</p>
        </div>
      )}

      {/* Info section */}
      <div className="bg-blue-50 rounded-xl p-6 border border-blue-100">
        <h3 className="font-semibold text-blue-800 mb-3 flex items-center gap-2">
          <Banknote className="w-5 h-5" />
          Comment payer mes charges ?
        </h3>
        <div className="space-y-3 text-blue-700 text-sm">
          <div className="flex items-start gap-3">
            <span className="bg-blue-200 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold flex-shrink-0">1</span>
            <p>Cliquez sur "Voir RIB" pour afficher les coordonnées bancaires de votre copropriété</p>
          </div>
          <div className="flex items-start gap-3">
            <span className="bg-blue-200 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold flex-shrink-0">2</span>
            <p>Vérifiez que l'IBAN correspond bien à celui de votre copropriété</p>
          </div>
          <div className="flex items-start gap-3">
            <span className="bg-blue-200 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold flex-shrink-0">3</span>
            <p>Effectuez un virement bancaire en indiquant la <strong>référence fournie</strong> dans le libellé</p>
          </div>
          <div className="flex items-start gap-3">
            <span className="bg-blue-200 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold flex-shrink-0">4</span>
            <p>Votre paiement sera enregistré par le gestionnaire après réception du virement</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MesCharges;
